import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { getFuncionario } from '../../api/funcionarios';
import { getFitTests } from '../../api/fitTests';
import { updateVinculo, getHistoricoSetor, getHistoricoFuncao } from '../../api/vinculos';
import { labelCiclo, ordinal } from '../../utils/ciclo';
import usePaginacao from '../../hooks/usePaginacao';
import Paginacao from '../../components/common/Paginacao';

export default function FuncionarioDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [funcionario, setFuncionario] = useState(null);
  const [tests, setTests] = useState([]);
  const [tab, setTab] = useState('dados');
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState({ empresa: '', tipo: '', equipamento: '' });
  const [historicoSetor, setHistoricoSetor] = useState({});   // {id_funcionario_empresa: [...]}
  const [historicoFuncao, setHistoricoFuncao] = useState({}); // {id_funcionario_empresa: [...]}

  const testsFiltrados = tests.filter(t =>
    (!filtro.empresa || t.funcionario_empresa?.empresa?.id_empresa === filtro.empresa) &&
    (!filtro.tipo || t.tipo_teste === filtro.tipo) &&
    (!filtro.equipamento || t.equipamento?.id_equipamento === filtro.equipamento)
  ).sort((a, b) => b.numero_ciclo - a.numero_ciclo);

  const pagHist = usePaginacao(testsFiltrados);

  const load = async () => {
    try {
      const [f, ts] = await Promise.all([
        getFuncionario(id),
        getFitTests({ id_funcionario: id }),
      ]);
      setFuncionario(f);
      setTests(ts);
      // Carrega histórico de setor e função para cada vínculo
      const setorMap = {}, funcaoMap = {};
      await Promise.all(
        f.vinculos.map(async v => {
          const [s, fn] = await Promise.all([
            getHistoricoSetor(v.id_funcionario_empresa).catch(() => []),
            getHistoricoFuncao(v.id_funcionario_empresa).catch(() => []),
          ]);
          setorMap[v.id_funcionario_empresa] = s;
          funcaoMap[v.id_funcionario_empresa] = fn;
        })
      );
      setHistoricoSetor(setorMap);
      setHistoricoFuncao(funcaoMap);
    } catch {
      // erro tratado — componente renderiza vazio
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [id]);

  const handleToggleVinculo = async (vinculo) => {
    if (vinculo.ativo && !window.confirm(`Deseja inativar o vínculo de ${funcionario?.nome} com ${vinculo.empresa?.razao_social}?`)) return;
    try {
      await updateVinculo(vinculo.id_funcionario_empresa, {
        ativo: !vinculo.ativo,
        data_inativacao: vinculo.ativo ? new Date().toISOString().slice(0, 10) : null,
      });
      load();
    } catch {
      alert('Erro ao atualizar vínculo. Tente novamente.');
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!funcionario) return null;

  const vinculoAtivo = funcionario.vinculos?.find(v => v.ativo);


  return (
    <>
      <button className="btn btn-sm btn-link text-muted mb-3 ps-0" onClick={() => navigate('/funcionarios')}>
        <i className="bi bi-arrow-left me-1"></i>Voltar para funcionários
      </button>

      <PageHeader
        title={funcionario.nome}
        subtitle={`CPF: ${funcionario.cpf}`}
        action={
          <div className="d-flex gap-2">
            {vinculoAtivo && (
              <button className="btn btn-sm btn-outline-danger" onClick={() => handleToggleVinculo(vinculoAtivo)}>
                <i className="bi bi-person-dash me-1"></i>Inativar vínculo
              </button>
            )}
            <button className="btn-datafit" style={{ height: 36, fontSize: '.85rem' }}
              onClick={() => navigate(`/fit-tests/novo?funcionario=${funcionario.id_funcionario}`)}>
              <i className="bi bi-plus me-1"></i>Registrar teste
            </button>
          </div>
        }
      />

      <div className="d-flex gap-0 border-bottom mb-4">
        {['dados', 'historico'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`btn btn-link text-decoration-none px-3 py-2 border-bottom border-2 ${tab === t ? 'border-primary fw-bold' : 'border-transparent text-muted'}`}
            style={{ borderColor: tab === t ? 'var(--main-color)' : 'transparent', color: tab === t ? 'var(--main-color)' : undefined }}>
            {t === 'dados' ? 'Dados do funcionário' : 'Histórico de ciclos'}
          </button>
        ))}
      </div>

      {/* ABA DADOS */}
      {tab === 'dados' && (
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card-datafit p-4">
              <div className="card-header-datafit mb-3" style={{ borderRadius: '0 12px 12px 0', display: 'inline-block' }}>
                Dados pessoais
              </div>
              <div className="row g-3">
                {[
                  ['Nome completo', funcionario.nome],
                  ['CPF', funcionario.cpf],
                  ['Data de nascimento', funcionario.data_nascimento || '—'],
                  ['Sexo', funcionario.sexo || '—'],
                ].map(([l, v]) => (
                  <div key={l} className="col-6">
                    <div className="small text-muted">{l}</div>
                    <div className="fw-500">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card-datafit p-4">
              <div className="card-header-datafit mb-3" style={{ borderRadius: '0 12px 12px 0', display: 'inline-block' }}>
                Vínculos
              </div>
              {funcionario.vinculos?.length === 0 && (
                <p className="text-muted small">Nenhum vínculo cadastrado.</p>
              )}
              {funcionario.vinculos?.map(v => {
                const setores = historicoSetor[v.id_funcionario_empresa] || [];
                const funcoes = historicoFuncao[v.id_funcionario_empresa] || [];
                const temHistorico = setores.length > 1 || funcoes.length > 1;
                return (
                  <div key={v.id_funcionario_empresa} className={`border rounded p-3 mb-2 ${!v.ativo ? 'opacity-50' : ''}`}>
                    <div className="d-flex justify-content-between">
                      <div className="fw-bold">{v.empresa?.razao_social}</div>
                      <StatusBadge status={v.ativo ? 'ativo' : 'inativo'} />
                    </div>

                    {/* Setor e Função atuais */}
                    <div className="d-flex gap-3 mt-2 flex-wrap">
                      {v.ultimo_setor && (
                        <span className="small">
                          <i className="bi bi-building me-1 text-muted"></i>
                          <span className="fw-500">Setor:</span> {v.ultimo_setor}
                        </span>
                      )}
                      {v.ultima_funcao && (
                        <span className="small">
                          <i className="bi bi-person-badge me-1 text-muted"></i>
                          <span className="fw-500">Função:</span> {v.ultima_funcao}
                        </span>
                      )}
                    </div>

                    <div className="small text-muted mt-1">
                      Desde {v.data_cadastro}
                      {v.ghe && <span className="ms-2 badge bg-secondary" style={{ fontSize: '.7rem' }}>GHE {v.ghe}</span>}
                    </div>

                    <div className="d-flex gap-3 mt-2">
                      {v.ultimo_ciclo_respiratorio && (
                        <span className="small"><i className="bi bi-lungs me-1 text-muted"></i>{labelCiclo(v.ultimo_ciclo_respiratorio)}</span>
                      )}
                      {v.ultimo_ciclo_auditivo && (
                        <span className="small"><i className="bi bi-ear me-1 text-muted"></i>{labelCiclo(v.ultimo_ciclo_auditivo)}</span>
                      )}
                    </div>

                    {/* Histórico de Setor e Função — exibe se houver mais de 1 registro */}
                    {temHistorico && (
                      <div className="mt-3 border-top pt-2">
                        <div className="small fw-bold text-muted text-uppercase mb-2" style={{ fontSize: '.7rem', letterSpacing: '.05em' }}>
                          <i className="bi bi-clock-history me-1"></i>Histórico
                        </div>
                        <div className="row g-2">
                          {setores.length > 0 && (
                            <div className="col-md-6">
                              <div className="small fw-500 mb-1"><i className="bi bi-building me-1"></i>Setores</div>
                              <div className="d-flex flex-column gap-1">
                                {setores.map((s, i) => (
                                  <div key={s.id_setor} className="d-flex align-items-center gap-2">
                                    <span className="small">{s.setor}</span>
                                    {i === 0 && <span className="badge bg-success" style={{ fontSize: '.65rem' }}>atual</span>}
                                    <span className="text-muted" style={{ fontSize: '.7rem' }}>
                                      {new Date(s.criado_em).toLocaleDateString('pt-BR')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {funcoes.length > 0 && (
                            <div className="col-md-6">
                              <div className="small fw-500 mb-1"><i className="bi bi-person-badge me-1"></i>Funções</div>
                              <div className="d-flex flex-column gap-1">
                                {funcoes.map((f, i) => (
                                  <div key={f.id_funcao} className="d-flex align-items-center gap-2">
                                    <span className="small">{f.funcao}</span>
                                    {i === 0 && <span className="badge bg-success" style={{ fontSize: '.65rem' }}>atual</span>}
                                    <span className="text-muted" style={{ fontSize: '.7rem' }}>
                                      {new Date(f.criado_em).toLocaleDateString('pt-BR')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {!v.ativo && v.data_inativacao && (
                      <div className="small text-danger mt-1">Inativado em {v.data_inativacao}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ABA HISTÓRICO */}
      {tab === 'historico' && (() => {
        const empresasDisponiveis = [...new Map(
          tests.map(t => [t.funcionario_empresa?.empresa?.id_empresa, t.funcionario_empresa?.empresa])
        ).entries()].filter(([id]) => id).map(([id, emp]) => ({ id, nome: emp?.nome_fantasia || emp?.razao_social }));

        const equipamentosDisponiveis = [...new Map(
          tests.map(t => [t.equipamento?.id_equipamento, t.equipamento])
        ).entries()].filter(([id]) => id).map(([id, eq]) => ({ id, label: `${eq?.ca} — ${eq?.tipo_mascara}` }));

        return (
        <div className="card-datafit">
          <div className="p-3 border-bottom d-flex gap-2 flex-wrap">
            <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 180 }}
              value={filtro.empresa} onChange={e => setFiltro(f => ({ ...f, empresa: e.target.value }))}>
              <option value="">Todas as empresas</option>
              {empresasDisponiveis.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
            <select className="form-select form-select-sm" style={{ width: 'auto' }}
              value={filtro.tipo} onChange={e => setFiltro(f => ({ ...f, tipo: e.target.value }))}>
              <option value="">Todos os tipos</option>
              <option value="respiratorio">Respiratório</option>
              <option value="auditivo">Auditivo</option>
            </select>
            <select className="form-select form-select-sm" style={{ width: 'auto', minWidth: 200 }}
              value={filtro.equipamento} onChange={e => setFiltro(f => ({ ...f, equipamento: e.target.value }))}>
              <option value="">Todos os equipamentos</option>
              {equipamentosDisponiveis.map(e => <option key={e.id} value={e.id}>{e.label}</option>)}
            </select>
            {(filtro.empresa || filtro.tipo || filtro.equipamento) && (
              <button className="btn btn-sm btn-outline-secondary"
                onClick={() => setFiltro({ empresa: '', tipo: '', equipamento: '' })}>
                <i className="bi bi-x me-1"></i>Limpar
              </button>
            )}
          </div>
          <div className="table-responsive">
            <table className="table table-datafit mb-0">
              <thead>
                <tr>
                  <th>Ciclo</th><th>Tipo</th><th>Empresa</th><th>Equipamento</th>
                  <th>Data agendada</th><th>Data realizada</th><th>Validade</th><th>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {pagHist.paginados.length === 0 ? (
                  <tr><td colSpan={8} className="text-center text-muted py-4">Nenhum teste encontrado</td></tr>
                ) : pagHist.paginados.map(t => (
                  <tr key={t.id_fit_test}>
                    <td>
                      <span className="badge rounded-pill" style={{ background: 'var(--main-color)', color: '#fff' }}>
                        {ordinal(t.numero_ciclo)}
                      </span>
                    </td>
                    <td>
                      <i className={`bi ${t.tipo_teste === 'auditivo' ? 'bi-ear' : 'bi-lungs'} me-1`}></i>
                      {t.tipo_teste}
                    </td>
                    <td className="small">{t.funcionario_empresa?.empresa?.nome_fantasia || t.funcionario_empresa?.empresa?.razao_social}</td>
                    <td>
                      <div>{t.equipamento?.ca}</div>
                      <div className="small text-muted">{t.equipamento?.tipo_mascara}</div>
                    </td>
                    <td>{t.data_agendada || '—'}</td>
                    <td>{t.data_realizada || '—'}</td>
                    <td>{t.data_validade || '—'}</td>
                    <td>
                      {t.resultado
                        ? <StatusBadge status={t.resultado.toLowerCase()} />
                        : <StatusBadge status={t.situacao} />
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Paginacao {...pagHist} />
        </div>
        );
      })()}
    </>
  );
}
